import { IsIn, IsOptional, IsString } from "class-validator"
import ProductCategory from "../../enums/ProductCategory"
import {
  TransformToIntOrNull,
  TransformToTrimmedStringArrayOrNull,
  TransformToTrimmedStringOrNull
} from "../../utils/transformations"

// Variable types reusable
export type OneToFiveType = 1 | 2 | 3 | 4 | 5
export const OneToFiveValues = [1, 2, 3, 4, 5]
export type YesNoMaybeType = "yes" | "no" | "maybe"
export const YesNoMaybeValues: YesNoMaybeType[] = ["yes", "no", "maybe"]

// Variable types specific
export type MainReasonType =
  | "onlyReport"
  | "noValue"
  | "expensive"
  | "availability"
  | "coach"
  | "misunderstood"
  | "groupCoaching"
  | "reachedGoal"
  | "other"
export const MainReasonValues: MainReasonType[] = [
  "onlyReport",
  "noValue",
  "expensive",
  "availability",
  "coach",
  "misunderstood",
  "groupCoaching",
  "reachedGoal",
  "other"
]
export const mainReasonMap = (category?: ProductCategory): Record<MainReasonType, string> => {
  return {
    ...(category === ProductCategory.REPORT && { onlyReport: "I only wanted the report" }),
    noValue:
      category === ProductCategory.COACHING
        ? "Not seeing value or progress"
        : "I didn’t find the coaching sessions helpful",
    expensive: category === ProductCategory.COACHING ? "Too expensive" : "I can't afford it",
    availability:
      category === ProductCategory.COACHING
        ? "Schedule or availability issues"
        : "The sessions don’t work with my schedule",
    ...(category === ProductCategory.COACHING && { coach: "Didn’t like my coach" }),
    ...(category === ProductCategory.REPORT && { misunderstood: "I  didn’t understand what I was signing up for" }),
    ...(category === ProductCategory.REPORT && {
      groupCoaching: "I don’t find group coaching helpful. I need 1:1 support"
    }),
    ...(category === ProductCategory.COACHING && { reachedGoal: "Reached my goal" }),
    ...(category === ProductCategory.COACHING && { other: "Other" })
  }
}
export const satisfactionRatingMap: Record<OneToFiveType, string> = {
  1: "Very dissatisfied",
  2: "Somewhat dissatisfied",
  3: "Neutral",
  4: "Somewhat satisfied",
  5: "Very satisfied"
}
export type ProgressType = "significant" | "some" | "little" | "none"
export const ProgressValues: ProgressType[] = ["significant", "some", "little", "none"]
export const progressMap: Record<ProgressType, string> = {
  significant: "Significant progress",
  some: "Some progress",
  little: "Little progress",
  none: "No progress"
}
export const valueRatingMap: Record<OneToFiveType, string> = {
  1: "Not at all",
  2: "Not really",
  3: "Somewhat",
  4: "Mostly",
  5: "Completely"
}
export type PossibleRetentionType =
  | "lowerPrice"
  | "differentCoach"
  | "sessionTime"
  | "structureAccountability"
  | "groupCoachingTime"
  | "jobRecommendations"
  | "pauseOption"
  | "other"
export const PossibleRetentionValues: PossibleRetentionType[] = [
  "lowerPrice",
  "differentCoach",
  "sessionTime",
  "structureAccountability",
  "groupCoachingTime",
  "jobRecommendations",
  "pauseOption",
  "other"
]
export const possibleRetentionMap = (category?: ProductCategory): Record<PossibleRetentionType, string> => {
  return {
    lowerPrice: "Lower price",
    ...(category === ProductCategory.COACHING && { differentCoach: "Different coach" }),
    ...(category === ProductCategory.COACHING && { sessionTime: "Different session times" }),
    ...(category === ProductCategory.COACHING && {
      structureAccountability: "More structure and accountability between sessions"
    }),
    ...(category === ProductCategory.REPORT && { groupCoachingTime: "Different group coaching times" }),
    ...(category === ProductCategory.REPORT && { jobRecommendations: "More Job Postings and recommendations" }),
    pauseOption: "Option to pause instead of cancel",
    other: "Other"
  }
}
export const stayForDiscountMap: Record<YesNoMaybeType, string> = {
  yes: "Yes, I'd stay with 25% off",
  maybe: "Maybe, depending on coach or timing",
  no: "No, I still want to cancel"
}

/**
 * This DTO is the expected request body for the post subscription cancellation endpoint
 */
export default class QuizPostSubscriptionCancellationIn {
  @IsString()
  @TransformToTrimmedStringOrNull()
  subscriptionId: string

  @IsIn(MainReasonValues)
  @TransformToTrimmedStringOrNull()
  mainReason: MainReasonType

  @IsIn(OneToFiveValues)
  @TransformToIntOrNull()
  satisfactionRating: OneToFiveType

  @IsIn(ProgressValues)
  @TransformToTrimmedStringOrNull()
  progress: ProgressType

  @IsIn(OneToFiveValues)
  @TransformToIntOrNull()
  valueRating: OneToFiveType

  @IsIn(PossibleRetentionValues, { each: true })
  @TransformToTrimmedStringArrayOrNull()
  possibleRetention: PossibleRetentionType[]

  @IsOptional()
  @IsIn(YesNoMaybeValues)
  @TransformToTrimmedStringOrNull()
  stayForDiscount?: YesNoMaybeType

  @IsOptional()
  @IsString()
  @TransformToTrimmedStringOrNull()
  mainReasonOther?: string

  @IsOptional()
  @IsString()
  @TransformToTrimmedStringOrNull()
  possibleRetentionOther?: string
}
